import { useState, useEffect, useRef, type FC } from "react";
import { Upload as AntUpload, message, Image, Modal } from "antd";
import { PlusOutlined, LoadingOutlined } from "@ant-design/icons";
import type { UploadProps, UploadFile } from "antd";
import http from "@/api/http";

interface UploadResponse {
  filename: string;
  message: string;
  size: number;
  url: string;
  user_id: number;
}

type UploadType = "image" | "video";

interface CustomUploadProps {
  type?: UploadType;
  value?: string | string[];
  onChange?: (url: string | string[]) => void;
  disabled?: boolean;
  maxSize?: number; // MB
  accept?: string;
  multiple?: boolean;
  useDeleteApi?: boolean;
  maxCount?: number;
}

const Upload: FC<CustomUploadProps> = ({
  type = "image",
  value,
  onChange,
  disabled = false,
  maxSize = 5,
  accept,
  multiple = false,
  useDeleteApi = true,
  maxCount,
}) => {
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const uploadingCountRef = useRef(0);

  const isVideo = type === "video";
  const endpoint = isVideo ? "/upload/video" : "/upload/image";
  const defaultAccept = isVideo ? "video/*" : "image/*";
  const fileAccept = accept || defaultAccept;

  // Sync external value with internal state (skip when uploads are in progress)
  useEffect(() => {
    if (uploadingCountRef.current > 0) return;
    if (value) {
      if (multiple && Array.isArray(value)) {
        setFileList(
          value.map((url, index) => ({
            uid: `-${index + 1}`,
            name: isVideo ? `video-${index + 1}.mp4` : `image-${index + 1}.png`,
            status: "done" as const,
            url,
          })),
        );
      } else if (!multiple && typeof value === "string") {
        setFileList([
          {
            uid: "-1",
            name: isVideo ? "video.mp4" : "image.png",
            status: "done",
            url: value,
          },
        ]);
      }
    } else {
      setFileList([]);
    }
  }, [value, isVideo, multiple]);

  const beforeUpload = (file: File) => {
    const isValidType = isVideo ? file.type.startsWith("video/") : file.type.startsWith("image/");

    if (!isValidType) {
      message.error(isVideo ? "Bạn chỉ có thể tải lên file video!" : "Bạn chỉ có thể tải lên file ảnh!");
      return false;
    }

    const isLtMaxSize = file.size / 1024 / 1024 < maxSize;
    if (!isLtMaxSize) {
      message.error(isVideo ? `Video phải nhỏ hơn ${maxSize}MB!` : `Ảnh phải nhỏ hơn ${maxSize}MB!`);
      return false;
    }

    return true;
  };

  const handleChange: UploadProps["onChange"] = (info) => {
    let newFileList = [...info.fileList];

    // Limit to 1 file for avatar, or respect multiple prop
    if (!multiple) {
      newFileList = newFileList.slice(-1);
    }

    // Update file status
    newFileList = newFileList.map((file) => {
      if (file.response) {
        file.url = file.response.url;
      }
      return file;
    });

    setFileList(newFileList);

    // Track active uploads to guard the useEffect sync
    uploadingCountRef.current = newFileList.filter((f) => f.status === "uploading").length;

    // Notify parent component
    if (info.file.status === "done") {
      if (multiple) {
        // Wait until all concurrent uploads finish before notifying parent
        if (uploadingCountRef.current === 0) {
          const allUrls = newFileList
            .filter((f) => f.status === "done" && (f.url || f.response?.url))
            .map((f) => f.url || f.response?.url) as string[];
          onChange?.(allUrls);
        }
      } else if (info.file.response?.url) {
        onChange?.(info.file.response.url);
      }
    } else if (info.file.status === "removed") {
      uploadingCountRef.current = 0;
      if (multiple) {
        const remainingUrls = newFileList
          .filter((f) => f.status === "done" && (f.url || f.response?.url))
          .map((f) => f.url || f.response?.url) as string[];
        onChange?.(remainingUrls);
      } else {
        onChange?.("");
      }
    }
  };

  const handleRemove: UploadProps["onRemove"] = async (file) => {
    const fileUrl = file.url || file.response?.url;

    if (fileUrl && useDeleteApi) {
      try {
        await http.post("/upload/remove", { url: fileUrl });
        message.success(isVideo ? "Xóa video thành công!" : "Xóa ảnh thành công!");
      } catch (error: any) {
        message.error(error?.response?.data?.message || (isVideo ? "Xóa video thất bại!" : "Xóa ảnh thất bại!"));
        return false;
      }
    }

    // Let handleChange handle the state update via info.file.status === "removed"
    return true;
  };

  const handleUpload: UploadProps["customRequest"] = async (options) => {
    const { file, onSuccess, onError, onProgress } = options;

    const formData = new FormData();
    formData.append(isVideo ? "video" : "image", file as File);

    setLoading(true);

    try {
      const response: UploadResponse = await http.post(endpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = progressEvent.total
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;
          onProgress?.({ percent: percentCompleted });
        },
      });

      if (response.url) {
        message.success(response.message || (isVideo ? "Tải video lên thành công!" : "Tải ảnh lên thành công!"));
        onSuccess?.(response);
      }
    } catch (error: any) {
      message.error(error?.response?.data?.message || (isVideo ? "Tải video lên thất bại!" : "Tải ảnh lên thất bại!"));
      onError?.(error);
    } finally {
      setLoading(false);
    }
  };

  const uploadButton = (
    <button style={{ border: 0, background: "none" }} type="button" disabled={loading}>
      {loading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>{isVideo ? "Tải lên video" : "Tải lên ảnh"}</div>
    </button>
  );

  const handlePreview = async (file: UploadFile) => {
    const url = file.url || file.response?.url;
    if (url) {
      setPreviewUrl(url);
      setPreviewOpen(true);
    }
  };

  return (
    <>
      <AntUpload
        name="file"
        listType="picture-card"
        className="upload-component"
        fileList={fileList}
        onChange={handleChange}
        onRemove={handleRemove}
        beforeUpload={beforeUpload}
        customRequest={handleUpload}
        disabled={disabled}
        accept={fileAccept}
        multiple={multiple}
        maxCount={multiple ? maxCount : 1}
        showUploadList={{
          showPreviewIcon: true,
          showRemoveIcon: true,
        }}
        onPreview={handlePreview}
      >
        {(!multiple && fileList.length >= 1) || (multiple && maxCount && fileList.length >= maxCount)
          ? null
          : uploadButton}
      </AntUpload>

      {isVideo ? (
        <Modal open={previewOpen} footer={null} onCancel={() => setPreviewOpen(false)} width={800}>
          <video src={previewUrl} controls style={{ width: "100%" }} />
        </Modal>
      ) : (
        previewUrl && (
          <Image
            style={{ display: "none" }}
            preview={{
              visible: previewOpen,
              onVisibleChange: (visible) => setPreviewOpen(visible),
            }}
            src={previewUrl}
          />
        )
      )}
    </>
  );
};

export default Upload;
